import { Box } from '@mui/material';
import { createContext, useContext } from 'react';
import { Link } from 'react-router-dom';
import { FaChevronRight } from 'react-icons/fa6';

const DataListContext = createContext({ template: '1fr' });

export function DataList({ columns, children, minWidth = 720 }) {
    const template = `${columns.map((column) => column.width || 'minmax(0, 1fr)').join(' ')} 1.5rem`;

    return (
        <DataListContext.Provider value={{ template, columns }}>
            <Box sx={{ overflowX: 'auto' }}>
                <Box sx={{ minWidth }}>
                    <Box
                        sx={{ ...styles.head, gridTemplateColumns: template }}
                        role="row"
                    >
                        {columns.map((column) => (
                            <Box
                                key={column.label}
                                role="columnheader"
                                sx={{ textAlign: column.align || 'left' }}
                            >
                                {column.label}
                            </Box>
                        ))}
                        <Box />
                    </Box>
                    {children}
                </Box>
            </Box>
        </DataListContext.Provider>
    );
}

export function DataCell({ align, children, sx }) {
    return (
        <Box
            sx={{
                textAlign: align,
                minWidth: 0,
                ...sx,
            }}
        >
            {children}
        </Box>
    );
}

export function DataRow({ to, children, showChevron = true }) {
    const { template } = useContext(DataListContext);
    const sx = {
        ...styles.row,
        gridTemplateColumns: template,
        ...(to ? styles.link : null),
    };

    const content = (
        <>
            {children}
            {showChevron && (
                <Box sx={styles.chevron} aria-hidden>
                    <FaChevronRight size={12} />
                </Box>
            )}
        </>
    );

    if (to) {
        return (
            <Box component={Link} to={to} role="row" sx={sx}>
                {content}
            </Box>
        );
    }

    return (
        <Box role="row" sx={sx}>
            {content}
        </Box>
    );
}

const styles = {
    head: {
        display: 'grid',
        alignItems: 'center',
        gap: 2,
        px: 0.5,
        pb: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        color: 'text.secondary',
        fontSize: '0.6875rem',
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
    },
    row: {
        display: 'grid',
        alignItems: 'center',
        gap: 2,
        px: 0.5,
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        minHeight: 56,
        textDecoration: 'none',
        color: 'inherit',
        transition: 'background-color .15s ease-out',
        '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
        },
        '&:last-of-type': {
            borderBottom: 'none',
        },
        '&:hover': {
            color: 'inherit',
            bgcolor: 'background.default',
        },
        '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'secondary.main',
            outlineOffset: -2,
        },
    },
    link: {
        cursor: 'pointer',
    },
    chevron: {
        display: 'flex',
        justifyContent: 'flex-end',
        color: 'text.secondary',
    },
};
